import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import useSeo from "../lib/useSeo";
import {
  Lock,
  Loader2,
  BarChart3,
  Package,
  Users,
  Mail,
  LogOut,
  Download,
  Truck,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Eye,
  EyeOff,
  TrendingUp,
  Sparkles,
  DollarSign,
  Send,
  CalendarClock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORAGE = "pb_admin_key_v1";

// ============================================================
// API client (single instance, X-Admin-Key header)
// ============================================================
const adminClient = (key) =>
  axios.create({
    baseURL: API,
    headers: { "X-Admin-Key": key, "Content-Type": "application/json" },
    timeout: 30000,
  });

// ============================================================
// Login screen
// ============================================================
function LoginScreen({ onSuccess }) {
  const [key, setKey] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!key.trim()) return;
    setLoading(true);
    try {
      await adminClient(key.trim()).post("/admin/login");
      localStorage.setItem(KEY_STORAGE, key.trim());
      toast.success("Welcome back!");
      onSuccess(key.trim());
    } catch (err) {
      toast.error(err?.response?.status === 401 ? "Invalid admin key." : "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-pb-cream px-5">
      <div className="bg-white rounded-[2rem] p-10 sm:p-12 shadow-[0_30px_80px_rgba(28,22,40,0.12)] border-4 border-white max-w-md w-full text-center relative overflow-hidden">
        <div className="blob bg-pb-coral" style={{ width: 260, height: 260, top: -80, right: -80, opacity: 0.18 }} />
        <div className="relative z-10">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-pb-sun text-pb-ink shadow-lg">
            <Lock className="h-7 w-7" />
          </div>
          <p className="mt-5 text-xs font-extrabold uppercase tracking-widest text-pb-coral">
            Plate Buddies HQ
          </p>
          <h1 className="font-display font-black text-3xl mt-2 leading-tight">
            Admin dashboard
          </h1>
          <p className="mt-2 text-pb-soft text-sm">
            Paste your admin key to manage orders, the pilot program, and your email list.
          </p>
          <form onSubmit={submit} className="mt-7 space-y-3">
            <div className="relative">
              <input
                data-testid="admin-key-input"
                type={show ? "text" : "password"}
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="pb_admin_..."
                autoFocus
                className="w-full rounded-2xl border-2 border-pb-sky/40 bg-pb-cream px-4 py-3.5 pr-12 font-mono text-pb-ink placeholder:text-pb-soft/60 focus:outline-none focus:border-pb-coral focus:bg-white transition text-sm"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-pb-soft hover:text-pb-coral"
                aria-label="Toggle visibility"
              >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <button
              data-testid="admin-login-submit"
              type="submit"
              disabled={loading || !key.trim()}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-pb-coral text-white font-extrabold px-6 py-3.5 shadow-[0_10px_24px_rgba(242,106,52,0.35)] hover:scale-[1.02] transition disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
              Unlock dashboard
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Stat tile
// ============================================================
function StatTile({ label, value, sub, accent = "#F26A34" }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-pb-sky/30 shadow-[0_8px_20px_rgba(28,22,40,0.05)]">
      <p className="text-[10px] font-extrabold uppercase tracking-widest text-pb-soft">{label}</p>
      <p className="font-display font-black text-3xl mt-2" style={{ color: accent }}>{value}</p>
      {sub && <p className="text-xs text-pb-soft mt-1">{sub}</p>}
    </div>
  );
}

// ============================================================
// Stats tab
// ============================================================
function StatsTab({ client }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/admin/stats");
      setStats(data);
    } catch (err) {
      toast.error("Failed to load stats.");
      console.warn("[admin/stats] failed", err);
    } finally {
      setLoading(false);
    }
  }, [client]);

  useEffect(() => { load(); }, [load]);

  if (loading && !stats) {
    return <div className="py-20 text-center"><Loader2 className="h-6 w-6 animate-spin text-pb-coral mx-auto" /></div>;
  }
  if (!stats) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-black text-2xl">Today at a glance</h2>
        <button
          data-testid="admin-stats-refresh"
          onClick={load}
          className="inline-flex items-center gap-2 text-sm font-bold text-pb-soft hover:text-pb-coral"
        >
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile label="Revenue today" value={`$${stats.revenue_today.toFixed(2)}`} sub={`${stats.orders_paid_total} total paid orders`} />
        <StatTile label="Orders to fulfill" value={stats.orders_to_fulfill} accent="#06A5A1" sub="Paid · packed · not yet shipped" />
        <StatTile label="Pilot pending" value={stats.pilot_pending} accent="#4E2C80" sub={`${stats.pilot_spots_remaining} spots left of 10`} />
        <StatTile label="Email signups" value={stats.email_signups_total} accent="#81C784" sub={`+${stats.email_signups_today} today`} />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <StatTile label="Custom Buddies generated" value={stats.custom_buddies_total} accent="#FFB74D" />
        <StatTile
          label="Interactive System reservations"
          value={stats.interactive_preorders_total ?? 0}
          accent="#4E2C80"
          sub={`${stats.interactive_preorders_reserved ?? 0} still awaiting contact`}
        />
      </div>
    </div>
  );
}

// ============================================================
// Orders tab
// ============================================================
const FULFILLMENT_STATES = ["new", "packed", "shipped", "delivered", "refunded"];

function OrdersTab({ client }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentFilter, setPaymentFilter] = useState("paid");
  const [fulfillFilter, setFulfillFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (paymentFilter) params.payment_status = paymentFilter;
      if (fulfillFilter) params.fulfillment_status = fulfillFilter;
      if (emailFilter) params.email = emailFilter;
      const { data } = await client.get("/admin/orders", { params });
      setOrders(data.orders || []);
    } catch (err) {
      toast.error("Failed to load orders.");
      console.warn("[admin/orders] failed", err);
    } finally {
      setLoading(false);
    }
  }, [client, paymentFilter, fulfillFilter, emailFilter]);

  useEffect(() => { load(); }, [load]);

  const updateOrder = async (sessionId, body) => {
    try {
      await client.patch(`/admin/orders/${sessionId}`, body);
      toast.success(body.fulfillment_status === "shipped" ? "Marked shipped — shipping email queued." : "Order updated.");
      load();
      setActive(null);
    } catch (err) {
      toast.error("Update failed.");
      console.warn("[admin/orders patch] failed", err);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display font-black text-2xl">Orders</h2>
        <button onClick={load} className="ml-auto inline-flex items-center gap-2 text-sm font-bold text-pb-soft hover:text-pb-coral">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>
      <div className="flex flex-wrap gap-3 bg-white rounded-2xl p-4 border border-pb-sky/30">
        <Select label="Payment" value={paymentFilter} onChange={setPaymentFilter} options={[
          { v: "", label: "All" },
          { v: "paid", label: "Paid" },
          { v: "initiated", label: "Abandoned" },
        ]} />
        <Select label="Fulfillment" value={fulfillFilter} onChange={setFulfillFilter} options={[
          { v: "", label: "All" },
          ...FULFILLMENT_STATES.map((s) => ({ v: s, label: s }))
        ]} />
        <div>
          <label className="block text-[10px] font-extrabold uppercase tracking-widest text-pb-soft mb-1">Email</label>
          <input
            value={emailFilter}
            onChange={(e) => setEmailFilter(e.target.value)}
            placeholder="someone@…"
            className="rounded-xl border-2 border-pb-sky/40 bg-pb-cream px-3 py-2 text-sm font-bold focus:outline-none focus:border-pb-coral focus:bg-white"
          />
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="h-5 w-5 animate-spin text-pb-coral mx-auto" /></div>
      ) : orders.length === 0 ? (
        <div className="py-12 text-center bg-white rounded-2xl text-pb-soft">No orders match those filters.</div>
      ) : (
        <div className="bg-white rounded-2xl border border-pb-sky/30 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-pb-cream text-[10px] uppercase tracking-widest text-pb-soft">
              <tr>
                <Th>Date</Th><Th>Email</Th><Th>Items</Th><Th>Total</Th><Th>Payment</Th><Th>Fulfillment</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.session_id} className="border-t border-pb-sky/20">
                  <Td>{(o.created_at || "").slice(0, 10)}</Td>
                  <Td>{o.email || <span className="text-pb-soft">—</span>}</Td>
                  <Td>{(o.items || []).map((i) => `${i.quantity}×${i.product_id}`).join(", ")}</Td>
                  <Td><span className="font-extrabold">${Number(o.amount || 0).toFixed(2)}</span></Td>
                  <Td><Badge status={o.payment_status} /></Td>
                  <Td><Badge status={o.fulfillment_status || "new"} /></Td>
                  <Td>
                    <button
                      onClick={() => setActive(o)}
                      className="text-xs font-extrabold text-pb-coral hover:underline"
                    >
                      Manage →
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {active && <OrderModal order={active} onClose={() => setActive(null)} onUpdate={updateOrder} />}
    </div>
  );
}

function OrderModal({ order, onClose, onUpdate }) {
  const [status, setStatus] = useState(order.fulfillment_status || "new");
  const [tracking, setTracking] = useState(order.tracking_number || "");
  const [carrier, setCarrier] = useState(order.tracking_carrier || "USPS");
  return (
    <div className="fixed inset-0 z-50 bg-pb-ink/60 flex items-center justify-center p-5" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-2xl w-full p-8 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-widest text-pb-coral">Order</p>
            <p className="font-mono text-xs text-pb-soft break-all">{order.session_id}</p>
            <p className="text-lg font-extrabold mt-1">{order.email || "—"}</p>
          </div>
          <button onClick={onClose} className="text-pb-soft hover:text-pb-coral text-2xl leading-none">×</button>
        </div>

        <div className="mt-5 grid sm:grid-cols-2 gap-3 text-sm">
          <Info label="Subtotal" value={`$${Number(order.subtotal || 0).toFixed(2)}`} />
          <Info label="Discount" value={`-$${Number(order.discount || 0).toFixed(2)}`} />
          <Info label="Shipping" value={order.shipping_label || `$${Number(order.shipping_amount || 0).toFixed(2)}`} />
          <Info label="Total" value={`$${Number(order.amount || 0).toFixed(2)}`} bold />
          <Info label="Coupon" value={order.coupon?.code || "—"} />
          <Info label="Payment" value={order.payment_status} />
        </div>

        {order.shipping_address && (
          <div className="mt-4 bg-pb-cream rounded-2xl p-4">
            <p className="text-[10px] font-extrabold uppercase tracking-widest text-pb-soft">Shipping address</p>
            <p className="text-sm mt-1 leading-relaxed">
              {order.shipping_address.full_name}<br />
              {order.shipping_address.line1}{order.shipping_address.line2 ? `, ${order.shipping_address.line2}` : ""}<br />
              {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.postal_code}<br />
              {order.shipping_address.country}{order.shipping_address.phone ? ` · ${order.shipping_address.phone}` : ""}
            </p>
          </div>
        )}

        <div className="mt-5 bg-pb-cream rounded-2xl p-4">
          <p className="text-[10px] font-extrabold uppercase tracking-widest text-pb-soft">Items</p>
          <ul className="mt-2 text-sm space-y-1">
            {(order.items || []).map((it, idx) => (
              <li key={`${it.product_id}-${idx}`} className="flex justify-between">
                <span>{it.quantity}× {it.name || it.product_id}{it.custom_buddy_name ? ` (${it.custom_buddy_name})` : ""}</span>
                <span className="font-extrabold">${(Number(it.unit_price || 0) * Number(it.quantity || 1)).toFixed(2)}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-6 border-t-2 border-dashed border-pb-sky/40 pt-5">
          <p className="text-xs font-extrabold uppercase tracking-widest text-pb-coral mb-3">Update fulfillment</p>
          <div className="grid sm:grid-cols-3 gap-3">
            <Select label="Status" value={status} onChange={setStatus} options={FULFILLMENT_STATES.map((s) => ({ v: s, label: s }))} />
            <Input label="Tracking #" value={tracking} onChange={setTracking} />
            <Input label="Carrier" value={carrier} onChange={setCarrier} />
          </div>
          <button
            data-testid="order-save"
            onClick={() => onUpdate(order.session_id, { fulfillment_status: status, tracking_number: tracking, carrier })}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-pb-coral text-white font-extrabold px-6 py-3 hover:scale-[1.02] transition shadow-[0_10px_24px_rgba(242,106,52,0.35)]"
          >
            <Truck className="h-4 w-4" /> Save & notify
          </button>
          {status === "shipped" && (
            <p className="text-xs text-pb-soft mt-2 text-center">A shipping notification email will be sent to {order.email || "the buyer"}.</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Pilot tab
// ============================================================
const PILOT_STATES = ["pending", "reviewed", "accepted", "rejected", "kit_shipped", "onboarded"];

function PilotTab({ client }) {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? { status: filter } : {};
      const { data } = await client.get("/admin/pilot/applications", { params });
      setApps(data.applications || []);
    } catch (err) {
      toast.error("Failed to load applications.");
      console.warn("[admin/pilot] failed", err);
    } finally {
      setLoading(false);
    }
  }, [client, filter]);

  useEffect(() => { load(); }, [load]);

  const updateApp = async (id, body) => {
    try {
      await client.patch(`/admin/pilot/applications/${id}`, body);
      const verb = body.status === "accepted" ? "Accepted — coupon email queued."
                 : body.status === "rejected" ? "Marked rejected — soft email queued."
                 : "Application updated.";
      toast.success(verb);
      load();
      setActive(null);
    } catch (err) {
      toast.error("Update failed.");
      console.warn("[admin/pilot patch] failed", err);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display font-black text-2xl">Founding Families</h2>
        <button onClick={load} className="ml-auto inline-flex items-center gap-2 text-sm font-bold text-pb-soft hover:text-pb-coral">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>
      <div className="flex flex-wrap gap-3 bg-white rounded-2xl p-4 border border-pb-sky/30">
        <Select label="Status" value={filter} onChange={setFilter} options={[
          { v: "", label: "All" },
          ...PILOT_STATES.map((s) => ({ v: s, label: s }))
        ]} />
      </div>

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="h-5 w-5 animate-spin text-pb-coral mx-auto" /></div>
      ) : apps.length === 0 ? (
        <div className="py-12 text-center bg-white rounded-2xl text-pb-soft">No applications yet — they'll land here the moment families apply.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {apps.map((a) => (
            <div key={a.id} data-testid={`pilot-card-${a.id}`} className="bg-white rounded-2xl p-5 border border-pb-sky/30 shadow-[0_6px_18px_rgba(28,22,40,0.05)]">
              <div className="flex justify-between items-start gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-display font-black text-lg leading-tight">{a.name}</p>
                  <p className="text-sm text-pb-soft truncate">{a.email}</p>
                  <p className="text-xs text-pb-soft mt-1">{a.city} · {a.kids_ages}</p>
                </div>
                <Badge status={a.status} />
              </div>
              <p className="text-sm mt-3 text-pb-ink/80 line-clamp-3">{a.struggle}</p>
              <button
                onClick={() => setActive(a)}
                className="mt-4 text-xs font-extrabold text-pb-coral hover:underline"
              >
                Review & decide →
              </button>
            </div>
          ))}
        </div>
      )}

      {active && <PilotModal app={active} onClose={() => setActive(null)} onUpdate={updateApp} />}
    </div>
  );
}

function PilotModal({ app, onClose, onUpdate }) {
  const [status, setStatus] = useState(app.status || "pending");
  const [notes, setNotes] = useState(app.admin_notes || "");
  return (
    <div className="fixed inset-0 z-50 bg-pb-ink/60 flex items-center justify-center p-5" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-2xl w-full p-8 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start">
          <div>
            <p className="font-display font-black text-2xl">{app.name}</p>
            <p className="text-sm text-pb-soft">{app.email}</p>
          </div>
          <button onClick={onClose} className="text-pb-soft hover:text-pb-coral text-2xl leading-none">×</button>
        </div>

        <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
          <Info label="City" value={app.city} />
          <Info label="Kids' ages" value={app.kids_ages} />
          <Info label="Instagram" value={app.instagram || "—"} />
          <Info label="Applied" value={(app.created_at || "").slice(0, 10)} />
        </div>

        <Section title="Their struggle" body={app.struggle} />
        <Section title="Current habits" body={app.habits} />
        <Section title="Why they're a fit" body={app.fit_reason} />

        <div className="mt-6 border-t-2 border-dashed border-pb-sky/40 pt-5">
          <p className="text-xs font-extrabold uppercase tracking-widest text-pb-coral mb-3">Decision</p>
          <Select label="Status" value={status} onChange={setStatus} options={PILOT_STATES.map((s) => ({ v: s, label: s }))} />
          <label className="block text-[10px] font-extrabold uppercase tracking-widest text-pb-soft mt-3 mb-1">Private notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="What stood out about this family?"
            rows={3}
            className="w-full rounded-xl border-2 border-pb-sky/40 bg-pb-cream px-3 py-2 text-sm font-bold focus:outline-none focus:border-pb-coral focus:bg-white resize-none"
          />
          <div className="mt-4 flex gap-2">
            <button
              data-testid="pilot-reject"
              onClick={() => onUpdate(app.id, { status: "rejected", admin_notes: notes })}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-white border-2 border-pb-sky/40 text-pb-soft hover:text-pb-coral hover:border-pb-coral font-bold px-4 py-3 transition text-sm"
            >
              <XCircle className="h-4 w-4" /> Decline (sends soft email)
            </button>
            <button
              data-testid="pilot-accept"
              onClick={() => onUpdate(app.id, { status: status === "rejected" ? "accepted" : status, admin_notes: notes })}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-pb-coral text-white font-extrabold px-4 py-3 hover:scale-[1.02] transition shadow-[0_8px_20px_rgba(242,106,52,0.35)] text-sm"
            >
              <CheckCircle2 className="h-4 w-4" /> Save status
            </button>
          </div>
          <p className="text-xs text-pb-soft mt-2 text-center">
            Choosing <b>accepted</b> sends the PILOT coupon email. <b>rejected</b> sends a thank-you with a 15% code.
          </p>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Email list tab
// ============================================================
function EmailListTab({ client, adminKey }) {
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/admin/email-list");
      setSignups(data.signups || []);
    } catch (err) {
      toast.error("Failed to load list.");
      console.warn("[admin/email-list] failed", err);
    } finally {
      setLoading(false);
    }
  }, [client]);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = () => {
    const url = `${API}/admin/email-list.csv`;
    fetch(url, { headers: { "X-Admin-Key": adminKey } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "plate-buddies-email-list.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
      })
      .catch((err) => {
        toast.error("Download failed.");
        console.warn("[csv] failed", err);
      });
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display font-black text-2xl">Email list <span className="text-pb-soft text-sm font-bold ml-2">{signups.length} subscribers</span></h2>
        <button
          data-testid="email-download-csv"
          onClick={downloadCsv}
          className="inline-flex items-center gap-2 rounded-full bg-pb-ink text-white font-extrabold px-5 py-2.5 hover:bg-pb-coral transition text-sm"
        >
          <Download className="h-4 w-4" /> Download CSV
        </button>
      </div>

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="h-5 w-5 animate-spin text-pb-coral mx-auto" /></div>
      ) : signups.length === 0 ? (
        <div className="py-12 text-center bg-white rounded-2xl text-pb-soft">No signups yet.</div>
      ) : (
        <div className="bg-white rounded-2xl border border-pb-sky/30 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-pb-cream text-[10px] uppercase tracking-widest text-pb-soft">
              <tr><Th>Email</Th><Th>Source</Th><Th>Joined</Th></tr>
            </thead>
            <tbody>
              {signups.map((s) => (
                <tr key={`${s.email}-${s.created_at}`} className="border-t border-pb-sky/20">
                  <Td>{s.email}</Td>
                  <Td>{s.source || "—"}</Td>
                  <Td>{(s.created_at || "").slice(0, 16).replace("T", " ")}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Interactive System pre-orders tab
// ============================================================
const PREORDER_STATUSES = ["reserved", "contacted", "converted", "declined"];

function PreordersTab({ client, adminKey }) {
  const [reservations, setReservations] = useState([]);
  const [stats, setStats] = useState({ total: 0, reserved: 0, converted: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = statusFilter ? `?status=${statusFilter}` : "";
      const { data } = await client.get(`/admin/preorders/interactive${q}`);
      setReservations(data.reservations || []);
      setStats(data.stats || { total: 0, reserved: 0, converted: 0 });
    } catch (err) {
      toast.error("Failed to load reservations.");
      console.warn("[admin/preorders] failed", err);
    } finally {
      setLoading(false);
    }
  }, [client, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (r, status) => {
    setSavingId(r.id);
    try {
      await client.patch(`/admin/preorders/interactive/${r.id}`, { status });
      toast.success(`Marked as ${status}.`);
      await load();
    } catch (err) {
      toast.error("Update failed.");
      console.warn("[admin/preorders/update] failed", err);
    } finally {
      setSavingId(null);
    }
  };

  const downloadCsv = () => {
    const url = `${API}/admin/preorders/interactive.csv`;
    fetch(url, { headers: { "X-Admin-Key": adminKey } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "plate-buddies-interactive-preorders.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
      })
      .catch((err) => {
        toast.error("Download failed.");
        console.warn("[csv] failed", err);
      });
  };

  return (
    <div data-testid="admin-preorders-tab" className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display font-black text-2xl">
            Interactive System reservations
            <span className="text-pb-soft text-sm font-bold ml-2">{stats.total} total</span>
          </h2>
          <p className="text-sm text-pb-soft mt-0.5">
            {stats.reserved} awaiting outreach · {stats.converted} converted to paid orders
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select
            label="Status"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[{ v: "", label: "All" }, ...PREORDER_STATUSES.map((s) => ({ v: s, label: s }))]}
          />
          <button
            data-testid="preorders-refresh"
            onClick={load}
            className="inline-flex items-center gap-2 rounded-full border-2 border-pb-sky/40 text-pb-ink font-extrabold px-4 py-2.5 hover:border-pb-coral hover:text-pb-coral transition text-sm"
          >
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
          <button
            data-testid="preorders-download-csv"
            onClick={downloadCsv}
            className="inline-flex items-center gap-2 rounded-full bg-pb-ink text-white font-extrabold px-5 py-2.5 hover:bg-pb-coral transition text-sm"
          >
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="h-5 w-5 animate-spin text-pb-coral mx-auto" /></div>
      ) : reservations.length === 0 ? (
        <div className="py-12 text-center bg-white rounded-2xl text-pb-soft">
          No reservations yet. Share the Shop page to start building the waitlist.
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-pb-sky/30 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-pb-cream text-[10px] uppercase tracking-widest text-pb-soft">
              <tr>
                <Th>#</Th>
                <Th>Parent</Th>
                <Th>Email</Th>
                <Th>Kids</Th>
                <Th>Notes</Th>
                <Th>Status</Th>
                <Th>Reserved</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {reservations.map((r) => (
                <tr key={r.id} data-testid={`preorder-row-${r.id}`} className="border-t border-pb-sky/20">
                  <Td><span className="font-display font-black text-pb-plum">#{r.position}</span></Td>
                  <Td>{r.parent_name || "—"}</Td>
                  <Td><a href={`mailto:${r.email}`} className="text-pb-coral font-bold hover:underline">{r.email}</a></Td>
                  <Td>{r.kids_ages || "—"}</Td>
                  <Td className="max-w-[260px]">
                    <span className="text-pb-soft text-xs whitespace-pre-wrap break-words">{r.notes || "—"}</span>
                  </Td>
                  <Td><Badge status={r.status || "reserved"} /></Td>
                  <Td className="text-pb-soft text-xs">{(r.created_at || "").slice(0, 16).replace("T", " ")}</Td>
                  <Td>
                    <select
                      data-testid={`preorder-status-${r.id}`}
                      disabled={savingId === r.id}
                      value={r.status || "reserved"}
                      onChange={(e) => updateStatus(r, e.target.value)}
                      className="rounded-lg border-2 border-pb-sky/40 bg-pb-cream px-2 py-1 text-xs font-bold focus:outline-none focus:border-pb-coral focus:bg-white disabled:opacity-50"
                    >
                      {PREORDER_STATUSES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Shared form bits
// ============================================================
function Select({ label, value, onChange, options }) {
  return (
    <div>
      <label className="block text-[10px] font-extrabold uppercase tracking-widest text-pb-soft mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-xl border-2 border-pb-sky/40 bg-pb-cream px-3 py-2 text-sm font-bold focus:outline-none focus:border-pb-coral focus:bg-white"
      >
        {options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
      </select>
    </div>
  );
}
function Input({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-[10px] font-extrabold uppercase tracking-widest text-pb-soft mb-1">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border-2 border-pb-sky/40 bg-pb-cream px-3 py-2 text-sm font-bold focus:outline-none focus:border-pb-coral focus:bg-white"
      />
    </div>
  );
}
function Th({ children }) { return <th className="text-left px-4 py-3 font-extrabold">{children}</th>; }
function Td({ children }) { return <td className="px-4 py-3 align-top">{children}</td>; }
function Info({ label, value, bold }) {
  return (
    <div className="bg-pb-cream rounded-xl p-3">
      <p className="text-[10px] font-extrabold uppercase tracking-widest text-pb-soft">{label}</p>
      <p className={`mt-0.5 text-sm ${bold ? "font-display font-black text-lg" : "font-bold"}`}>{value}</p>
    </div>
  );
}
function Section({ title, body }) {
  if (!body) return null;
  return (
    <div className="mt-4">
      <p className="text-[10px] font-extrabold uppercase tracking-widest text-pb-coral">{title}</p>
      <p className="mt-1 text-sm leading-relaxed">{body}</p>
    </div>
  );
}
function Badge({ status }) {
  const colors = {
    paid: "#81C784", initiated: "#FFB74D",
    new: "#B39DDB", packed: "#FFD54F", shipped: "#4DD0E1", delivered: "#81C784", refunded: "#F26A34",
    pending: "#FFD54F", reviewed: "#4DD0E1", accepted: "#81C784", rejected: "#F26A34", kit_shipped: "#B39DDB", onboarded: "#4E2C80",
    reserved: "#4E2C80", contacted: "#4DD0E1", converted: "#81C784", declined: "#F26A34",
  };
  const c = colors[status] || "#7c7e92";
  return <span className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-widest" style={{ background: `${c}33`, color: c }}>{status}</span>;
}

// ============================================================
// Root
// ============================================================
export default function Admin() {
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(KEY_STORAGE) || "");
  const [tab, setTab] = useState("stats");

  useSeo({
    title: "Admin | Plate Buddies",
    description: "Plate Buddies admin dashboard.",
    url: "https://platebuddies.com/admin",
    noIndex: true,
  });

  const logout = () => {
    localStorage.removeItem(KEY_STORAGE);
    setAdminKey("");
    toast.success("Signed out.");
  };

  if (!adminKey) return <LoginScreen onSuccess={setAdminKey} />;

  const client = adminClient(adminKey);

  const TABS = [
    { id: "stats", label: "Stats", icon: BarChart3 },
    { id: "growth", label: "Growth", icon: TrendingUp },
    { id: "orders", label: "Orders", icon: Package },
    { id: "pilot", label: "Pilot", icon: Users },
    { id: "preorders", label: "Pre-orders", icon: CalendarClock },
    { id: "email", label: "Email list", icon: Mail },
  ];

  return (
    <div data-testid="page-admin" className="min-h-screen bg-pb-cream font-body">
      <header className="bg-white border-b border-pb-sky/30 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3 flex items-center gap-4">
          <img src="/brand/logo-wordmark.png" alt="Plate Buddies" className="h-8 w-auto" />
          <span className="hidden sm:inline text-xs font-extrabold uppercase tracking-widest text-pb-coral">Admin</span>
          <nav className="ml-4 sm:ml-6 flex gap-1 overflow-x-auto">
            {TABS.map((t) => (
              <button
                key={t.id}
                data-testid={`admin-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 rounded-full px-3 sm:px-4 py-1.5 text-sm font-extrabold transition whitespace-nowrap ${
                  tab === t.id ? "bg-pb-coral text-white" : "text-pb-soft hover:text-pb-ink"
                }`}
              >
                <t.icon className="h-3.5 w-3.5" /> {t.label}
              </button>
            ))}
          </nav>
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            data-testid="admin-test-generator-link"
            className="ml-auto hidden md:inline-flex items-center gap-1.5 rounded-full border border-pb-coral/40 px-3 py-1.5 text-xs font-extrabold text-pb-coral hover:bg-pb-coral hover:text-white transition-colors"
          >
            <Sparkles className="h-3.5 w-3.5" /> Test the Buddy maker
          </a>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="md:ml-0 ml-auto inline-flex items-center gap-1.5 text-xs font-bold text-pb-soft hover:text-pb-coral"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        {tab === "stats" && <StatsTab client={client} />}
        {tab === "growth" && <GrowthTab client={client} />}
        {tab === "orders" && <OrdersTab client={client} />}
        {tab === "pilot" && <PilotTab client={client} />}
        {tab === "preorders" && <PreordersTab client={client} adminKey={adminKey} />}
        {tab === "email" && <EmailListTab client={client} adminKey={adminKey} />}
      </main>
    </div>
  );
}

// ============================================================
// Growth tab — conversion funnel + abandoned-checkout recovery
// ============================================================
function GrowthTab({ client }) {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recovery, setRecovery] = useState(null);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [postPurchase, setPostPurchase] = useState(null);
  const [postPurchaseLoading, setPostPurchaseLoading] = useState(false);

  const loadMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/admin/funnel-metrics");
      setMetrics(data);
    } catch (err) {
      toast.error("Couldn't load funnel metrics.");
    } finally {
      setLoading(false);
    }
  }, [client]);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  const runRecoveryDryRun = async () => {
    setRecoveryLoading(true);
    try {
      const { data } = await client.post("/admin/run-abandoned-recovery", { dry_run: true, limit: 50 });
      setRecovery(data);
      toast.success(`Found ${data.matched} recoverable checkouts.`);
    } catch (err) {
      toast.error("Recovery preview failed.");
    } finally {
      setRecoveryLoading(false);
    }
  };

  const sendRecoveryEmails = async () => {
    if (!window.confirm(`Send abandoned-checkout emails to ${recovery?.matched || 0} shoppers? This can't be undone.`)) return;
    setRecoveryLoading(true);
    try {
      const { data } = await client.post("/admin/run-abandoned-recovery", { dry_run: false, limit: 50 });
      toast.success(`Sent ${data.sent} recovery emails 🎁`);
      setRecovery(data);
      await loadMetrics();
    } catch (err) {
      toast.error("Recovery send failed.");
    } finally {
      setRecoveryLoading(false);
    }
  };

  const runPostPurchaseDryRun = async () => {
    setPostPurchaseLoading(true);
    try {
      const { data } = await client.post("/admin/run-post-purchase-upsell", { dry_run: true, limit: 50 });
      setPostPurchase(data);
      toast.success(`Found ${data.matched} paid customers eligible for upsell.`);
    } catch (err) {
      toast.error("Upsell preview failed.");
    } finally {
      setPostPurchaseLoading(false);
    }
  };

  const sendPostPurchase = async () => {
    if (!window.confirm(`Send 10%-off thank-you emails to ${postPurchase?.matched || 0} paid customers?`)) return;
    setPostPurchaseLoading(true);
    try {
      const { data } = await client.post("/admin/run-post-purchase-upsell", { dry_run: false, limit: 50 });
      toast.success(`Sent ${data.sent} thank-you emails 💛`);
      setPostPurchase(data);
      await loadMetrics();
    } catch (err) {
      toast.error("Upsell send failed.");
    } finally {
      setPostPurchaseLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-pb-soft"><Loader2 className="h-4 w-4 animate-spin" /> Loading funnel…</div>;
  }
  if (!metrics) return <div className="text-pb-soft">Couldn&apos;t load metrics.</div>;

  const window7 = metrics.last_7d;
  const window30 = metrics.last_30d;
  const all = metrics.all_time;

  return (
    <div data-testid="admin-growth-tab" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display font-black text-2xl text-pb-ink">Growth · Funnel</h2>
          <p className="text-sm text-pb-soft mt-0.5">Real-time conversion metrics + one-click recovery emails.</p>
        </div>
        <button
          data-testid="growth-refresh"
          onClick={loadMetrics}
          className="inline-flex items-center gap-1.5 rounded-full text-xs font-extrabold text-pb-coral hover:underline"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {/* Revenue cards */}
      <div className="grid sm:grid-cols-3 gap-3">
        <MetricCard
          testId="metric-revenue-all"
          icon={DollarSign}
          label="Paid revenue · all time"
          value={`$${all.paid_revenue_usd.toFixed(2)}`}
          sub={`${all.checkouts_paid} orders`}
          tone="green"
        />
        <MetricCard
          testId="metric-recoverable"
          icon={Sparkles}
          label="Recoverable revenue"
          value={`$${all.recoverable_revenue_usd.toFixed(2)}`}
          sub={`${all.abandoned_with_email} abandoned checkouts w/ email`}
          tone="coral"
        />
        <MetricCard
          testId="metric-conv-rate"
          icon={TrendingUp}
          label="Checkout conversion"
          value={`${all.checkout_conversion_rate}%`}
          sub={`${all.checkouts_paid} paid / ${all.checkouts_initiated} started`}
          tone="ink"
        />
      </div>

      {/* Funnel table */}
      <div className="bg-white rounded-3xl p-6 shadow-sm border border-pb-sky/20">
        <h3 className="font-display font-black text-lg text-pb-ink mb-4">Conversion funnel</h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] font-extrabold uppercase tracking-widest text-pb-soft border-b border-pb-sky/30">
              <th className="text-left py-2">Stage</th>
              <th className="text-right py-2">Last 7d</th>
              <th className="text-right py-2">Last 30d</th>
              <th className="text-right py-2">All time</th>
            </tr>
          </thead>
          <tbody className="font-bold text-pb-ink">
            <FunnelRow label="Email signups" a={window7.email_signups} b={window30.email_signups} c={all.email_signups} />
            <FunnelRow label="Buddies generated" a={window7.buddies_generated} b={window30.buddies_generated} c={all.buddies_generated} />
            <FunnelRow label="Checkouts started" a={window7.checkouts_initiated} b={window30.checkouts_initiated} c={all.checkouts_initiated} />
            <FunnelRow label="Checkouts paid" a={window7.checkouts_paid} b={window30.checkouts_paid} c={all.checkouts_paid} accent />
            <FunnelRow label="Paid revenue" a={`$${window7.paid_revenue_usd.toFixed(2)}`} b={`$${window30.paid_revenue_usd.toFixed(2)}`} c={`$${all.paid_revenue_usd.toFixed(2)}`} accent />
          </tbody>
        </table>
      </div>

      {/* Abandoned checkout recovery */}
      <div data-testid="recovery-panel" className="bg-white rounded-3xl p-6 shadow-sm border border-pb-sky/20">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="min-w-0">
            <h3 className="font-display font-black text-lg text-pb-ink">Abandoned-checkout recovery</h3>
            <p className="text-sm text-pb-soft mt-1">
              Sends a warm &quot;come back — 15% off with <b>COMEBACK15</b>&quot; email to shoppers who hit Stripe but bailed (2h+ old, with an email attached). Idempotent — each shopper is nudged once.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="recovery-preview"
              onClick={runRecoveryDryRun}
              disabled={recoveryLoading}
              className="rounded-full bg-pb-cream border-2 border-pb-sky/40 text-pb-ink font-extrabold text-xs px-4 py-2 hover:border-pb-coral hover:text-pb-coral transition"
            >
              {recoveryLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" /> : "Preview"}
            </button>
            <button
              data-testid="recovery-send"
              onClick={sendRecoveryEmails}
              disabled={recoveryLoading || !recovery || recovery.matched === 0}
              className="rounded-full bg-pb-coral text-white font-extrabold text-xs px-4 py-2 hover:scale-105 transition disabled:opacity-40"
            >
              <Send className="h-3.5 w-3.5 inline mr-1" />
              Send {recovery ? `${recovery.matched}` : ""} email{recovery && recovery.matched === 1 ? "" : "s"}
            </button>
          </div>
        </div>
        {recovery && (
          <div className="mt-4 rounded-2xl bg-pb-cream border border-pb-sky/30 p-3 max-h-64 overflow-y-auto">
            {recovery.items.length === 0 ? (
              <p className="text-sm text-pb-soft text-center py-4">Nothing to recover right now — nice work! 🎉</p>
            ) : (
              <ul className="text-xs space-y-1.5" data-testid="recovery-list">
                {recovery.items.map((it, i) => (
                  <li key={it.session_id} className="flex items-center justify-between gap-3">
                    <span className="text-pb-ink font-bold truncate">
                      {i + 1}. {it.email} · <span className="text-pb-soft font-normal">{(it.items || []).join(", ")}</span>
                    </span>
                    <span className="text-pb-coral font-extrabold flex-shrink-0">${(it.amount || 0).toFixed(2)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Post-purchase upsell */}
      <div data-testid="upsell-panel" className="bg-white rounded-3xl p-6 shadow-sm border border-pb-sky/20">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="min-w-0">
            <h3 className="font-display font-black text-lg text-pb-ink">Post-purchase thank-you + repeat discount</h3>
            <p className="text-sm text-pb-soft mt-1">
              Sends a warm &quot;hey, how&apos;s your Buddy? here&apos;s 10% off with <b>AGAIN10</b>&quot; to customers who paid 72h+ ago. Runs once per order.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="upsell-preview"
              onClick={runPostPurchaseDryRun}
              disabled={postPurchaseLoading}
              className="rounded-full bg-pb-cream border-2 border-pb-sky/40 text-pb-ink font-extrabold text-xs px-4 py-2 hover:border-pb-coral hover:text-pb-coral transition"
            >
              {postPurchaseLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" /> : "Preview"}
            </button>
            <button
              data-testid="upsell-send"
              onClick={sendPostPurchase}
              disabled={postPurchaseLoading || !postPurchase || postPurchase.matched === 0}
              className="rounded-full bg-pb-ink text-white font-extrabold text-xs px-4 py-2 hover:scale-105 transition disabled:opacity-40"
            >
              <Send className="h-3.5 w-3.5 inline mr-1" />
              Send {postPurchase ? `${postPurchase.matched}` : ""} email{postPurchase && postPurchase.matched === 1 ? "" : "s"}
            </button>
          </div>
        </div>
        {postPurchase && (
          <div className="mt-4 rounded-2xl bg-pb-cream border border-pb-sky/30 p-3 max-h-64 overflow-y-auto">
            {postPurchase.items.length === 0 ? (
              <p className="text-sm text-pb-soft text-center py-4">No customers ready for their thank-you nudge yet.</p>
            ) : (
              <ul className="text-xs space-y-1.5">
                {postPurchase.items.map((it, i) => (
                  <li key={it.session_id} className="text-pb-ink font-bold">
                    {i + 1}. {it.email} · <span className="text-pb-soft font-normal">{it.created_at?.slice(0, 10)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function MetricCard({ testId, icon: Icon, label, value, sub, tone = "ink" }) {
  const tones = {
    green: "bg-pb-green/10 text-pb-green",
    coral: "bg-pb-coral/10 text-pb-coral",
    ink: "bg-pb-ink/10 text-pb-ink",
  };
  return (
    <div data-testid={testId} className="bg-white rounded-3xl p-5 shadow-sm border border-pb-sky/20">
      <div className={`inline-flex h-9 w-9 items-center justify-center rounded-xl ${tones[tone] || tones.ink}`}>
        <Icon className="h-4 w-4" />
      </div>
      <p className="text-[11px] font-extrabold uppercase tracking-widest text-pb-soft mt-3">{label}</p>
      <p className="font-display font-black text-2xl sm:text-3xl text-pb-ink mt-1">{value}</p>
      {sub && <p className="text-xs text-pb-soft mt-1">{sub}</p>}
    </div>
  );
}

function FunnelRow({ label, a, b, c, accent }) {
  return (
    <tr className="border-b border-pb-sky/10 last:border-b-0">
      <td className={`py-2.5 ${accent ? "text-pb-coral font-extrabold" : ""}`}>{label}</td>
      <td className="py-2.5 text-right">{a ?? 0}</td>
      <td className="py-2.5 text-right">{b ?? 0}</td>
      <td className={`py-2.5 text-right ${accent ? "text-pb-coral font-extrabold" : ""}`}>{c ?? 0}</td>
    </tr>
  );
}
